import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { CreateCustomsCalculationRequest, CreateCustomsProductRequest } from '../../../services/api/customs-calculations';
import { CalculationProgressService } from '../../../services/calculation-progress';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { GuestSessionService } from '../../../services/guest-session';
import { CreateCalculationFormComponent } from '../../shared/customs-calculations/create-calculation-form/create-calculation-form';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-create-calculation',
  standalone: true,
  imports: [CommonModule, RouterModule, CreateCalculationFormComponent],
  templateUrl: './create-calculation.html',
  styleUrl: './create-calculation.scss'
})
export class CreateCalculationComponent implements OnInit, OnDestroy {
  submitting = false;
  error: string | null = null;
  invoiceProgressText = 'Идёт расчёт по инвойсу. Это может занять несколько минут...';
  errorCreatingCalculationText = 'Ошибка при создании расчета';
  errorCreatingFromInvoiceText = 'Ошибка при создании расчета из инвойса';
  errorProcessingInvoiceText = 'Ошибка при обработке инвойса';
  errorAddingProductText = 'Ошибка при добавлении товара. Возможно, AI расчет не удался.';
  invoiceUploadingText = 'Загружаем инвойс...';
  invoiceSavedRecognizingText = 'Инвойс сохранён. Запускаем распознавание...';

  private subscriptions = new Subscription();

  constructor(
    private calculationProgress: CalculationProgressService,
    private languageService: LanguageService,
    private authService: AuthService,
    private guestSession: GuestSessionService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
      })
    );
    // Страница публичная: не редиректим на login. Телефон здесь не спрашиваем — только после расчёта.
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    const tr = (key: string, current: string) => this.languageService.translate(key) || current;
    this.invoiceProgressText = tr('public.createCalculation.invoiceProgress', this.invoiceProgressText);
    this.errorCreatingCalculationText = tr('public.createCalculation.errorCreatingCalculation', this.errorCreatingCalculationText);
    this.errorCreatingFromInvoiceText = tr('public.createCalculation.errorCreatingFromInvoice', this.errorCreatingFromInvoiceText);
    this.errorProcessingInvoiceText = tr('public.createCalculation.errorProcessingInvoice', this.errorProcessingInvoiceText);
    this.errorAddingProductText = tr('public.createCalculation.errorAddingProduct', this.errorAddingProductText);
    this.invoiceUploadingText = tr('public.createCalculation.invoiceUploading', this.invoiceUploadingText);
    this.invoiceSavedRecognizingText = tr('public.createCalculation.invoiceSavedRecognizing', this.invoiceSavedRecognizingText);
  }

  onCalculationCreated(data: { calculation: CreateCustomsCalculationRequest; products: CreateCustomsProductRequest[] }): void {
    this.withSession(
      () => this.calculationProgress.startManual(data.calculation, data.products, {
        first: this.invoiceProgressText,
        processingError: this.errorCreatingCalculationText,
        addProductError: this.errorAddingProductText
      }),
      this.errorCreatingCalculationText
    );
  }

  onInvoiceUploaded(data: { file: File; title?: string }): void {
    this.withSession(
      () => this.calculationProgress.startInvoice(data.file, data.title, {
        first: this.invoiceUploadingText,
        invoiceSaved: this.invoiceSavedRecognizingText,
        processingError: this.errorProcessingInvoiceText
      }),
      this.errorCreatingFromInvoiceText
    );
  }

  /** Незалогиненному клиенту сайт сам заводит гостевую запись (уникальный код вместо телефона) и считает от её имени. */
  private withSession(start: () => Observable<number>, errorText: string): void {
    this.submitting = true;
    this.error = null;
    this.cdr.markForCheck();
    this.subscriptions.add(
      this.guestSession.ensureSession().subscribe({
        next: () => this.openCabinet(start(), errorText),
        error: err => {
          console.error('Error creating guest session:', err);
          this.error = errorText;
          this.submitting = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Как только сервер выдал номер — сразу в кабинет расчёта: ход работы ИИ клиент видит там. */
  private openCabinet(created$: Observable<number>, errorText: string): void {
    this.subscriptions.add(
      created$.subscribe({
        next: id => {
          this.submitting = false;
          this.router.navigate(['/calculations', id, 'overview']);
        },
        error: err => {
          console.error('Error creating calculation:', err);
          this.error = errorText;
          this.submitting = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onCancel(): void {
    this.navigateToDashboard();
  }

  private navigateToDashboard(): void {
    const user = this.authService.getUser();
    const roleCode = user?.role?.code;

    switch (roleCode) {
      case 'ROLE_CARGO_OWNER':
        this.router.navigate(['/cargo-owner/dashboard/calculations']);
        break;
      case 'ROLE_FREIGHT_FORWARDER':
        this.router.navigate(['/freight-forwarder/dashboard']);
        break;
      case 'ROLE_CUSTOMS_BROKER':
        this.router.navigate(['/customs-broker/dashboard']);
        break;
      case 'ROLE_PUBLIC':
      default:
        this.router.navigate(['/public/dashboard']);
        break;
    }
  }
}
